import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, ArrowLeft, Building2, MoreHorizontal, FileText, Shield } from 'lucide-react';
import { supabase } from '../../services/supabase';
import { useOrganizationContext } from '../../contexts/OrganizationContext';
import { useSidebar, SidebarMenu, SidebarMenuItem } from '../ui/Sidebar';
import { Button } from '../ui/Button';
import { SimpleMenu, SimpleMenuItem } from '../ui/Menu/SimpleMenu';

export type AppMode = 'creator' | 'admin' | 'org';

interface SidebarUserFooterProps {
  /** Current app mode - determines what switch link to show */
  mode: AppMode;
}

export function SidebarUserFooter({ mode }: SidebarUserFooterProps) {
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const { isOrgUser, isSystemAdmin, userContext } = useOrganizationContext();
  const userName = userContext?.username || userContext?.email?.split('@')[0] || 'User';
  const userEmail = userContext?.email || 'user@example.com';

  const organizations = useMemo(
    () => (userContext?.organizations || []).map((m) => m.organization),
    [userContext?.organizations]
  );

  const handleSignOut = async () => {
    if (isMobile) {
      setOpenMobile(false);
    }
    await supabase.auth.signOut();
    navigate('/');
  };

  const handleModeSwitch = () => {
    if (isMobile) {
      setOpenMobile(false);
    }

    if (mode === 'creator') {
      if (isSystemAdmin) {
        navigate('/admin/dashboard');
      } else if (organizations && organizations.length > 0) {
        const firstOrg = organizations[0];
        navigate(`/org/${firstOrg.slug}/dashboard`);
      }
    } else {
      navigate('/dashboard');
    }
  };

  // Determine if we should show the switch link
  const shouldShowSwitchLink = () => {
    if (mode === 'creator') {
      return organizations.length > 0 || isSystemAdmin;
    }
    if (isOrgUser) {
      return false;
    }
    return true;
  };

  const showSwitchLink = shouldShowSwitchLink();
  const switchLabel = mode === 'creator' ? 'Organization Mode' : 'Creator Mode';
  const SwitchIcon = mode === 'creator' ? Building2 : ArrowLeft;
  const consumerBaseUrl = import.meta.env.VITE_CONSUMER_BASE_URL || 'https://videreo.com';

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md hover:bg-sidebar-accent">
          <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
            <span className="truncate font-medium">{userName}</span>
            <span className="text-muted-foreground truncate text-xs">{userEmail}</span>
          </div>

          <SimpleMenu
            align={isMobile ? 'left' : 'right'}
            menuClassName="min-w-56 rounded-lg"
            position="right-bottom"
            zIndexClassName="z-[2000]"
            trigger={
              <Button
                variant="ghost"
                size="sm"
                className="h-10 w-10 p-0 bg-white/80 hover:bg-white border border-gray-200 rounded-full"
                aria-label="User menu"
              >
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            }
          >
            <div className="p-2 text-left text-sm">
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{userName}</span>
                <span className="text-muted-foreground truncate text-xs">{userEmail}</span>
              </div>
            </div>

            <div className="my-1 h-px bg-gray-200" />

            {showSwitchLink && (
              <>
                <SimpleMenuItem onClick={handleModeSwitch} className="text-base py-1.5">
                  <SwitchIcon className="mr-2 h-4 w-4" />
                  {switchLabel}
                </SimpleMenuItem>
                <div className="my-1 h-px bg-gray-200" />
              </>
            )}

            {mode === 'creator' && (
              <>
                <a
                  href={`${consumerBaseUrl}/terms-of-service`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center px-3 py-1.5 text-sm hover:bg-gray-100"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Terms of Service
                </a>
                <a
                  href={`${consumerBaseUrl}/privacy`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center px-3 py-1.5 text-sm hover:bg-gray-100"
                >
                  <Shield className="mr-2 h-4 w-4" />
                  Privacy Policy
                </a>
                <div className="my-1 h-px bg-gray-200" />
              </>
            )}

            <SimpleMenuItem
              onClick={handleSignOut}
              className="text-red-600 hover:text-red-700 text-base py-1.5"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </SimpleMenuItem>
          </SimpleMenu>
        </div>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
